import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, IconButton, Box, useTheme, useMediaQuery } from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Notification from './components/common/Notification';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import BarberDashboard from './components/barber/BarberDashboard';
import ClientDashboard from './components/client/ClientDashboard';
import BookingFlow from './components/client/BookingFlow';
import Navbar from './components/common/Navbar';
import ProtectedRoute from './components/common/ProtectedRoute';
import { lightTheme, darkTheme } from './theme';
import BarberProfile from './components/client/BarberProfile';
import BarberList from './components/client/BarberList';
import BottomNav from './components/common/BottomNav';
import UserProfile from './components/profile/UserProfile';
import BarberShopList from './components/shop/BarberShopList';
import Shop from './components/client/Shop';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import { setupNotifications } from './services/api';
import ThemeLanguageSwitcher from './components/common/ThemeLanguageSwitcher';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const AppRoutes: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Setup notifications for the API service
  useEffect(() => {
    setupNotifications(showNotification);
  }, [showNotification]);

  return (
    <Box sx={{ 
      pb: { 
        xs: '65px',
        sm: '70px'
      }, 
      pt: { 
        xs: '48px',
        sm: 0
      },
      zIndex: 1
    }}>
      <Navbar/>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/barber/*"
          element={
            <ProtectedRoute userType="barber">
              <BarberDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/*"
          element={
            <ProtectedRoute userType="client">
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<BookingFlow />} />
        <Route path="/barbers" element={<BarberList />} />
        <Route path="/barbers/:barberId" element={<BarberProfile />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute userType={user?.type || 'client'}>
              <UserProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/barber-shops"
          element={<BarberShopList />}
        />
        <Route
          path="/shop/:barberId"
          element={<Shop />}
        />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute userType="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute userType="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
      </Routes>
      <BottomNav />
      <Notification />
    </Box>
  );
};

const App: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false);

  const handleToggleTheme = () => {
    setDarkMode(!darkMode);
  };

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <LanguageProvider>
        <NotificationProvider>
          <AuthProvider>
            <Router>
              <ThemeLanguageSwitcher 
                darkMode={darkMode} 
                onToggleTheme={handleToggleTheme} 
              />
              <AppRoutes />
            </Router>
          </AuthProvider>
        </NotificationProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
};

export default App; 